import { KeyRound, Sparkles } from 'lucide-react';
import type { ComponentType } from 'react';
import type { ModelProviderSecret } from '@reflex/shared';
import { MODEL_PROVIDER_LABELS } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { ResourcePreviewCard } from '../resource-preview/ResourcePreviewCard';
import { modelProviderSecretDisplayName } from './secret-display';

const KIND_LABEL = 'Provider key';

/**
 * Glyph that, on its own, tells a subscription apart from a metered API
 * key — `Sparkles` for subscriptions, a key for everything else. Mirrors
 * {@link ModelProviderSecretChip} so the same icon language reads across
 * the chip and its preview.
 */
const TYPE_ICON: Record<ModelProviderSecret['type'], ComponentType<{ className?: string }>> = {
  apiKey: KeyRound,
  subscription: Sparkles,
};

const TYPE_LABEL: Record<ModelProviderSecret['type'], string> = {
  apiKey: 'API key',
  subscription: 'Subscription',
};

const TYPE_BADGE_VARIANT: Record<ModelProviderSecret['type'], 'secondary' | 'outline'> = {
  apiKey: 'secondary',
  subscription: 'outline',
};

/**
 * Subscriptions read with the same success-toned outline used on the costs
 * page so they look visually distinct from metered API keys everywhere the
 * preview is rendered, not only in the costs UI.
 */
const TYPE_BADGE_CLASS: Record<ModelProviderSecret['type'], string> = {
  apiKey: '',
  subscription: 'border-status-success/40 text-status-success',
};

const SCOPE_LABEL: Record<ModelProviderSecret['scope'], string> = {
  user: 'Personal',
  team: 'Team',
  org: 'Organization',
};

export interface ModelProviderSecretPreviewCardProps {
  /** The secret to summarise. */
  secret: ModelProviderSecret;
  /**
   * Optional management-page link template (e.g. `/org/costs/keys/:id`).
   * When supplied, `ResourcePreviewCard` renders an "Open provider key"
   * footer button resolving `:id` to the secret's id. Omit on surfaces
   * with no detail destination (e.g. an inline picker).
   */
  detailHref?: string;
  className?: string;
}

/**
 * Rich preview of a model-provider secret built on the shared
 * {@link ResourcePreviewCard}, so a key surfaced inline reads identically
 * to the User / Organization / costs-key previews: a circular glyph, display
 * name, kind · id, provider as a status pill, the raw `mps_*` id, and a row of meta
 * badges (type + scope). Pure render — no fetching, and deliberately no
 * embedded interactive `OwnerChip`: nesting one inside a Popover focus-traps
 * it open, and the scope badge already conveys whose key it is, matching the
 * other previews' structure.
 */
export function ModelProviderSecretPreviewCard({
  secret,
  detailHref,
  className,
}: ModelProviderSecretPreviewCardProps) {
  const Icon = TYPE_ICON[secret.type];

  const meta = (
    <>
      <Badge
        variant={TYPE_BADGE_VARIANT[secret.type]}
        className={cn('px-1.5 py-0 text-[10px]', TYPE_BADGE_CLASS[secret.type])}
      >
        {TYPE_LABEL[secret.type]}
      </Badge>
      <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
        {SCOPE_LABEL[secret.scope]}
      </Badge>
    </>
  );

  return (
    <ResourcePreviewCard
      kindLabel={KIND_LABEL}
      icon={Icon}
      name={modelProviderSecretDisplayName(secret)}
      subtitle={MODEL_PROVIDER_LABELS[secret.provider]}
      meta={meta}
      detailHref={detailHref}
      id={secret.id}
      openLinkLabel="Open provider key"
      className={className}
    />
  );
}
